import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import AppButton from '../atoms/AppButton';
import { IconChevronDown, IconImage } from '../atoms/Icon';
import useConfirmDialog from './ConfirmDialog';
import { useToast } from '../contexts/ToastContext';
import useImageUpload from '../hooks/useImageUpload';
import { parseDecimalInput, sanitizeDecimalInput } from '../utils/currency';
import { titleCase } from '../utils/text';

function formatPrice(price) {
  return Number(price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function CompactImagePicker({ value, onChange, size = 34 }) {
  const fileInputRef = useRef(null);
  const { upload, uploading, error } = useImageUpload();

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const url = await upload(file);
    if (url) onChange(url);
  }

  return (
    <div className="compact-image-picker" style={{ position: 'relative', flexShrink: 0 }}>
      <div
        onClick={() => !uploading && fileInputRef.current?.click()}
        title={value ? 'Trocar foto' : 'Adicionar foto'}
        style={{
          width: size,
          height: size,
          borderRadius: 8,
          border: value ? 'none' : '1.5px dashed #ddd',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: uploading ? 'default' : 'pointer',
          overflow: 'hidden',
        }}
      >
        {uploading ? (
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            style={{ width: 14, height: 14, color: 'var(--color-purple-primary)' }}
          />
        ) : value ? (
          <img src={value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <IconImage size={size * 0.45} style={{ color: '#ccc' }} />
        )}
      </div>

      {value && !uploading && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(''); }}
          aria-label="Remover foto"
          className="compact-image-picker-remove"
        >
          ×
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {error && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 2, fontSize: 10, color: '#e74c3c', whiteSpace: 'nowrap', zIndex: 2 }}>
          {error}
        </div>
      )}
    </div>
  );
}

/**
 * `flag` liga um booleano extra por item, mostrado como um chip alternável ao
 * lado do switch de disponibilidade: { field, label, onTitle, offTitle }.
 * Usado pelo meio a meio, que é por variação (uma delas pode não se dividir).
 */
const NestedItemsManager = forwardRef(function NestedItemsManager(
  // `allowFree` liga o toggle "Pago"/"Gratuito": só os adicionais usam (uma
  // variação sempre tem preço próprio, então variantes nunca passam essa prop).
  { title, addLabel, emptyLabel, itemLabel = 'Item', feminine = false, flag, allowFree = false, items, onCreate, onUpdate, onDelete, onChange },
  ref,
) {
  const [adding, setAdding] = useState(false);
  const [draftName, setDraftName] = useState('');
  const [draftPrice, setDraftPrice] = useState('');
  const [draftImageUrl, setDraftImageUrl] = useState('');
  const [draftIsPaid, setDraftIsPaid] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editIsPaid, setEditIsPaid] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const { confirm, dialog } = useConfirmDialog();
  const toast = useToast();

  function startEdit(item) {
    setEditingId(item.id);
    setEditName(item.name);
    // Vírgula, como se escreve preço em português; o parse aceita as duas.
    setEditPrice(String(item.price).replace('.', ','));
    setEditImageUrl(item.imageUrl || '');
    setEditIsPaid(item.isPaid ?? true);
  }

  // Devolve se deu certo (ou se não havia nada a fazer) — quem fecha o modal
  // usa isso pra saber se pode fechar ou se precisa manter aberto mostrando o
  // erro, em vez de descartar um rascunho válido em silêncio.
  async function handleAdd() {
    if (!draftName.trim() || (!allowFree || draftIsPaid) && !(parseDecimalInput(draftPrice) > 0)) {
      setError(!allowFree || draftIsPaid ? 'Informe um nome e um preço maior que zero.' : 'Informe um nome.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { name: titleCase(draftName.trim()), imageUrl: draftImageUrl };
      if (allowFree) {
        payload.isPaid = draftIsPaid;
        payload.price = draftIsPaid ? parseDecimalInput(draftPrice) : null;
      } else {
        payload.price = parseDecimalInput(draftPrice);
      }
      const created = await onCreate(payload);
      onChange([...items, created]);
      setDraftName('');
      setDraftPrice('');
      setDraftImageUrl('');
      setDraftIsPaid(true);
      setAdding(false);
      toast.success(`${itemLabel} ${feminine ? 'adicionada' : 'adicionado'} com sucesso!`);
      return true;
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveEdit(item) {
    if (!editName.trim() || (!allowFree || editIsPaid) && !(parseDecimalInput(editPrice) > 0)) {
      setError(!allowFree || editIsPaid ? 'Informe um nome e um preço maior que zero.' : 'Informe um nome.');
      return false;
    }
    setBusy(true);
    setError(null);
    try {
      const payload = { name: titleCase(editName.trim()), imageUrl: editImageUrl };
      if (allowFree) {
        payload.isPaid = editIsPaid;
        payload.price = editIsPaid ? parseDecimalInput(editPrice) : null;
      } else {
        payload.price = parseDecimalInput(editPrice);
      }
      const updated = await onUpdate(item.id, payload);
      onChange(items.map((i) => (i.id === item.id ? updated : i)));
      setEditingId(null);
      toast.success(`${itemLabel} ${feminine ? 'atualizada' : 'atualizado'} com sucesso!`);
      return true;
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  }

  // Chamado por quem embrulha este componente (o modal) antes de fechar: se
  // houver uma edição em andamento, tenta salvar; se houver um rascunho de
  // item novo com algo digitado, tenta adicionar. Draft totalmente vazio (o
  // usuário só abriu "+ Adicionar" e desistiu sem escrever nada) é descartado
  // sem erro — não há o que perder. Retorna `false` só quando há algo digitado
  // e inválido, pra quem chama manter o modal aberto com o erro visível em vez
  // de fechar e descartar silenciosamente.
  useImperativeHandle(ref, () => ({
    async flush() {
      if (editingId) {
        const item = items.find((i) => i.id === editingId);
        if (!item) return true;
        return handleSaveEdit(item);
      }
      if (adding) {
        if (!draftName.trim() && !draftPrice.trim()) {
          setAdding(false);
          setDraftImageUrl('');
          return true;
        }
        return handleAdd();
      }
      return true;
    },
  }));

  async function handleToggleAvailable(item) {
    setBusy(true);
    setError(null);
    try {
      const updated = await onUpdate(item.id, { isAvailable: !item.isAvailable });
      onChange(items.map((i) => (i.id === item.id ? updated : i)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggleFlag(item) {
    setBusy(true);
    setError(null);
    try {
      const updated = await onUpdate(item.id, { [flag.field]: !item[flag.field] });
      onChange(items.map((i) => (i.id === item.id ? updated : i)));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleImageChange(item, url) {
    setBusy(true);
    setError(null);
    try {
      const updated = await onUpdate(item.id, { imageUrl: url });
      onChange(items.map((i) => (i.id === item.id ? updated : i)));
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(item) {
    const ok = await confirm(`Excluir "${item.name}"?`, {
      title: 'Excluir item',
      confirmLabel: 'Excluir',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      await onDelete(item.id);
      onChange(items.filter((i) => i.id !== item.id));
      toast.success(`${itemLabel} ${feminine ? 'excluída' : 'excluído'} com sucesso!`);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span
          className="fw-semibold"
          style={{ fontSize: 12, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}
        >
          {title}
        </span>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="action-btn"
            style={{ fontSize: 12, padding: '3px 8px' }}
          >
            + {addLabel}
          </button>
        )}
      </div>

      {error && (
        <Alert variant="danger" className="py-1 px-2 mb-2" style={{ fontSize: 12, borderRadius: 8 }}>
          {error}
        </Alert>
      )}

      {items.length === 0 && !adding && (
        <div className="text-center text-muted py-3" style={{ fontSize: 12, backgroundColor: '#fafafa', borderRadius: 8 }}>
          {emptyLabel}
        </div>
      )}

      {items.map((item) => (
        <div
          key={item.id}
          className="nested-item-row d-flex align-items-center gap-2 mb-2 p-2"
          style={{ backgroundColor: '#fafafa', borderRadius: 8 }}
        >
          {editingId === item.id ? (
            <>
              <CompactImagePicker value={editImageUrl} onChange={setEditImageUrl} />
              <Form.Control
                size="sm"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Nome"
                autoCapitalize="sentences"
                autoCorrect="off"
                enterKeyHint="next"
              />
              {allowFree && (
                <Form.Check
                  type="switch"
                  label="Pago"
                  checked={editIsPaid}
                  onChange={(e) => setEditIsPaid(e.target.checked)}
                  className="flex-shrink-0"
                />
              )}
              {(!allowFree || editIsPaid) && (
                <Form.Control
                  size="sm"
                  type="text"
                  inputMode="decimal"
                  className="nested-item-price"
                  value={editPrice}
                  onChange={(e) => setEditPrice(sanitizeDecimalInput(e.target.value))}
                  placeholder="Preço"
                  enterKeyHint="done"
                />
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => handleSaveEdit(item)}
                className="action-btn"
                style={{ fontSize: 12, height: 34, padding: '0 10px', flexShrink: 0 }}
              >
                Salvar
              </button>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="action-btn action-btn-danger"
                style={{ fontSize: 12, height: 34, padding: '0 10px', flexShrink: 0 }}
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <CompactImagePicker value={item.imageUrl} onChange={(url) => handleImageChange(item, url)} />
              <div className="flex-grow-1" style={{ fontSize: 13, opacity: item.isAvailable ? 1 : 0.5 }}>
                {item.name}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-neutral)', opacity: item.isAvailable ? 1 : 0.5 }}>
                {allowFree && item.isPaid === false ? 'Gratuito' : `R$ ${formatPrice(item.price)}`}
              </div>
              {flag && (
                <button
                  type="button"
                  onClick={() => handleToggleFlag(item)}
                  disabled={busy}
                  aria-pressed={!!item[flag.field]}
                  title={item[flag.field] ? flag.onTitle : flag.offTitle}
                  className={`flag-chip${item[flag.field] ? ' on' : ''}`}
                >
                  {flag.label}
                </button>
              )}
              <Form.Check
                type="switch"
                checked={item.isAvailable}
                onChange={() => handleToggleAvailable(item)}
                disabled={busy}
                title={item.isAvailable ? 'Disponível' : 'Indisponível'}
              />
              <button
                type="button"
                onClick={() => startEdit(item)}
                className="action-btn action-btn-muted"
                style={{ fontSize: 12, padding: '3px 8px' }}
              >
                Editar
              </button>
              <button
                type="button"
                onClick={() => handleDelete(item)}
                disabled={busy}
                className="action-btn action-btn-danger"
                style={{ fontSize: 12, padding: '3px 8px' }}
              >
                Excluir
              </button>
            </>
          )}
        </div>
      ))}

      {adding && (
        <div className="nested-item-row d-flex align-items-center gap-2 mb-2 p-2" style={{ backgroundColor: '#fafafa', borderRadius: 8 }}>
          <CompactImagePicker value={draftImageUrl} onChange={setDraftImageUrl} />
          <Form.Control
            size="sm"
            autoFocus
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            placeholder="Nome"
            autoCapitalize="sentences"
            autoCorrect="off"
            enterKeyHint="next"
          />
          {allowFree && (
            <Form.Check
              type="switch"
              label="Pago"
              checked={draftIsPaid}
              onChange={(e) => setDraftIsPaid(e.target.checked)}
              className="flex-shrink-0"
            />
          )}
          {(!allowFree || draftIsPaid) && (
            <Form.Control
              size="sm"
              type="text"
              inputMode="decimal"
              className="nested-item-price"
              value={draftPrice}
              onChange={(e) => setDraftPrice(sanitizeDecimalInput(e.target.value))}
              placeholder="Preço"
              enterKeyHint="done"
            />
          )}
          <AppButton
            variant="primary"
            size="sm"
            onClick={handleAdd}
            disabled={busy}
            style={{ fontSize: 12, height: 34, padding: '0 12px', flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Adicionar
          </AppButton>
          <button
            type="button"
            onClick={() => { setAdding(false); setDraftName(''); setDraftPrice(''); setDraftImageUrl(''); setDraftIsPaid(true); }}
            className="action-btn action-btn-danger d-flex align-items-center justify-content-center"
            style={{ height: 34, width: 34, padding: 0, flexShrink: 0 }}
            aria-label="Cancelar"
          >
            <IconChevronDown size={14} style={{ transform: 'rotate(180deg)' }} />
          </button>
        </div>
      )}

      {dialog}
    </div>
  );
});

export default NestedItemsManager;
